import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

/** TRANSLATION */
import { useTranslation } from 'react-i18next'

/** API */
import CardTypesApi from 'api/setting/finances/card-type.api'

/** UTILS || SLICE || CONSTANTS */
import { resetModal, submitModal, unSubmitModal } from 'app/slice/modal.slice'
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils'

/** COMPONENT */
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import { Box, Grid, InputAdornment, Typography } from '@mui/material'
import { fileUrl } from 'api/api'
import HeaderCard from 'component/CardCustom/HeaderCard'
import { notiError, notiSuccess } from 'component/notifications/notifications'
import TableCustom from 'component/Table/TableCustom'
import TableRowCommon from 'component/Table/TableRowCommon'
import UploadImage from 'component/Upload/UploadImage'
import { imgs, onImgError } from 'constants/images'
import _ from 'lodash'
import CheckboxButton from 'new-components/buttons/checkbox-button'
import MainButton from 'new-components/buttons/main-button'
import SwitchButton from 'new-components/buttons/switch-button'
import SelectInput from 'new-components/inputs/select-input'
import TextInput from 'new-components/inputs/text-input'
import AlertModal from 'new-components/modal/alert-modal'
import PageModal from 'new-components/modal/page-modal'
import SnackbarComponent from 'new-components/snackbar/snackbar-component'


const permissions = {
  isCreate: isCreate(),
  isDelete: isDelete(),
  isUpdate: isUpdate(),
  isView: isView()
}

const initStateErrorMessage = {
  NOT_FOUND_CARD_TYPE: ``,
  CARD_TYPE_ALREADY_SYSTEM: ``,
  INVALID_CREATED: ``,
  INVALID_UPDATED: ``,
  IS_DUPLICATE_CARD_TYPE_SHORTNAME: ``,
  IS_DUPLICATE_CARD_TYPE_NAME: ``,
  CARD_TYPE_SHORTNAME: ``,
  CARD_TYPE_SHORTNAME_STRING_BASE: ``,
  CARD_TYPE_SHORTNAME_STRING_EMPTY: ``,
  CARD_TYPE_SHORTNAME_MAXIMUM_LENGTH: ``,
  CARD_TYPE_SHORTNAME_ANY_REQUIRED: ``,
  CARD_TYPE_NAME: ``,
  CARD_TYPE_NAME_STRING_BASE: ``,
  CARD_TYPE_NAME_STRING_EMPTY: ``,
  CARD_TYPE_NAME_MAXIMUM_LENGTH: ``,
  CARD_TYPE_NAME_ANY_REQUIRED: ``,

  CARD_TYPE_NAME_EN: ``,
  CARD_TYPE_NAME_EN_STRING_BASE: ``,
  CARD_TYPE_NAME_EN_STRING_EMPTY: ``,
  CARD_TYPE_NAME_EN_MAXIMUM_LENGTH: ``,
  CARD_TYPE_NAME_EN_ANY_REQUIRED: ``
}
export default function CardTypes() {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [cardTypes, setCardTypes] = useState([])

  const [cardTypeId, setCardTypeId] = useState(Number)
  const [cardTypeShortname, setCardTypeShortname] = useState('')
  const [cardTypeName, setCardTypeName] = useState('')
  const [hasCredit, setHasCredit] = useState('NOT_SUPPORT')
  const [hasDebit, setHasDebit] = useState('NOT_SUPPORT')
  const [cardTypeStatus, setCardTypeStatus] = useState('NOT_SUPPORT')
  const [image, setImage] = useState({ fileId: null, base64: '', fileExtension: '', fileSize: '' })
  const [imgData, setImgData] = useState<string | null>(null)

  const [errorMessage, setErrorMessage] = useState(initStateErrorMessage)

  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('createAt')
  const [sortType, setSortType] = useState('ASC')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('ALL')
  const [disabledSubmit, setDisabledSubmit] = useState(false)
  const [isOpenAlertModal, setIsOpenAlertModal] = useState(false)
  const [statusAlertModal, setStatusAlertModal] = useState('')
  const [id, setId] = useState<number>(0)
  const [name, setName] = useState('')
  const [isOpenSnack, setIsOpenSnack] = useState(false)
  const [typeSnack, setTypeSnack] = useState<"success" | "error" | "warning" | "info" | any>("success")
  const [labelSnack, setLabelSnack] = useState('')

  const [isPopup, setIsPopup] = useState(false)

  const loadData = async () => {
    try {
      const resp = await CardTypesApi.findAll({
        page: page,
        pageLimit: pageLimit,
        search: search,
        sortBy: sortBy,
        sortType: sortType,
        status: status === 'ALL' ? '' : status
      })
      if (resp.status === 200) {
        setRowCount(resp.headers['x-total'])
        setCardTypes(resp.data)
      } else {
        setCardTypes([])
      }
    } catch (error) {
      notiError('ERROR')
    }
  }

  useEffect(() => {
    loadData()
  }, [page, pageLimit, sortBy, sortType])

  const submit = () => {

    if (!cardTypeShortname || !cardTypeName) {
      return setErrorMessage({ ...errorMessage, ...{ CARD_TYPE_SHORTNAME: !cardTypeShortname ? t(`CARD_TYPE.MESSAGE.CARD_TYPE_SHORTNAME`) : '', CARD_TYPE_NAME: !cardTypeName ? t(`CARD_TYPE.MESSAGE.CARD_TYPE_NAME`) : '' } })
    }

    dispatch(submitModal())
    setDisabledSubmit(true)
    CardTypesApi.create({ cardTypeShortname, cardTypeName, hasCredit, hasDebit, image })
      .then((res: any) => {
        if (res.status !== undefined && res.status === 201) {
          notiSuccess(t(`CARD_TYPE.MESSAGE.SUCCESS.CREATE`), '', null, null)
          resetForm()

          loadData()
          dispatch(resetModal())
        } else {
          const err = res.response.data
          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`CARD_TYPE.MESSAGE.${err.message}`) } })
          dispatch(unSubmitModal())
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`CARD_TYPE.MESSAGE.${err.message}`) } })
        dispatch(unSubmitModal())
      }).finally(() => setDisabledSubmit(false))
  }

  const submitEdit = () => {

    if (!cardTypeShortname || !cardTypeName) {
      return setErrorMessage({ ...errorMessage, ...{ CARD_TYPE_SHORTNAME: !cardTypeShortname ? t(`CARD_TYPE.MESSAGE.CARD_TYPE_SHORTNAME`) : '', CARD_TYPE_NAME: !cardTypeName ? t(`CARD_TYPE.MESSAGE.CARD_TYPE_NAME`) : '' } })
    }

    dispatch(submitModal())
    setDisabledSubmit(true)
    CardTypesApi.update(cardTypeId, { cardTypeShortname, cardTypeName, hasCredit, hasDebit, status: cardTypeStatus, image })
      .then((res: any) => {
        if (res.status !== undefined && res.status === 200) {
          notiSuccess(t(`CARD_TYPE.MESSAGE.SUCCESS.UPDATE`), '', null, null)
          resetForm()
          loadData()
          dispatch(resetModal())
        } else {
          const err = res.response.data
          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`CARD_TYPE.MESSAGE.${err.message}`) } })
          dispatch(unSubmitModal())
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`CARD_TYPE.MESSAGE.${err.message}`) } })
        dispatch(unSubmitModal())
      }).finally(() => setDisabledSubmit(false))
  }

  const clearErrorMessage = () => {
    setErrorMessage(initStateErrorMessage)
  }

  const resetForm = () => {
    setCardTypeId(0)
    setCardTypeShortname('')
    setCardTypeName('')
    setHasDebit('NOT_SUPPORT')
    setHasCredit('NOT_SUPPORT')
    setImgData('')
    setImage({ fileId: null, base64: '', fileExtension: '', fileSize: '' })
    setIsPopup(false)
    clearErrorMessage()
  }
  const onActive = () => {
    if (id) {
      CardTypesApi.updateStatus(id, statusAlertModal).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('CARD_TYPE.MESSAGE.SUCCESS.UPDATE_STATUS'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('CARD_TYPE.MESSAGE.ERROR'))
        }
      })
    }
  }
  const onDelete = () => {
    if (id) {
      CardTypesApi.remove(id).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('CARD_TYPE.MESSAGE.SUCCESS.DELETE'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('CARD_TYPE.MESSAGE.ERROR'))
        }
      })
    }
  }

  const onEdit = (data: any) => {
    const { cardTypeId: cdTypeId, cardTypeShortname: cdTypeShortName, cardTypeName: cdTypeName, hasDebit: cdTypeHasDebit, hasCredit: cdTypeHasCredit, status: cdTypeStatus, image: cdTypeImage } = data

    setCardTypeId(cdTypeId)
    setCardTypeShortname(cdTypeShortName)
    setCardTypeName(cdTypeName)
    setHasDebit(cdTypeHasDebit)
    setHasCredit(cdTypeHasCredit)
    setCardTypeStatus(cdTypeStatus)

    if (cdTypeImage) {
      setImgData(`${fileUrl}/${cdTypeImage.filePath}/${cdTypeImage.fileName}`)
      setImage({ fileId: data.image.fileId, base64: '', fileExtension: '', fileSize: '' })
    }

    setIsPopup(true)
  }

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangePage = (val: number) => {
    setPage(val)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
  }

  const handleChangeStatus = (statusVal: string) => {
    setStatus(statusVal)
  }

  const uploadImage = (files: any) => {
    if (files[0]) {
      /**
       * files all detail file
       * lastModified, lastModifiedDate
       * name, size, type
       */
      const file = files[0]
      const fileName = file.name.split('.')
      const detailImage = {
        fileId: image.fileId,
        base64: '',
        fileExtension: fileName[fileName.length - 1],
        fileSize: file.size.toString()
      }
      const reader: any = new FileReader()
      reader.addEventListener('load', () => {
        /** reader.result is base64 */
        const base64 = reader.result.split(',')
        setImgData(reader.result)
        detailImage.base64 = base64.length > 0 ? base64[base64.length - 1] : reader.result
      })
      reader.readAsDataURL(file)
      setImage(detailImage)
    }
  }

  const headCells = [
    { id: 'no', disablePadding: false, label: '#' },
    { id: 'cardTypeLogo', disablePadding: false, align: 'center', label: t('CARD_TYPE.TABLE.CELL.CARD_TYPE_LOGO'), width: '200px' },
    { id: 'cardTypeShortname', disablePadding: false, label: t('CARD_TYPE.TABLE.CELL.CARD_TYPE_SHORTNAME') },
    { id: 'cardTypeName', disablePadding: false, label: t('CARD_TYPE.TABLE.CELL.CARD_TYPE_NAME') },
    { id: 'hasDebit', disablePadding: false, align: 'center', label: t('CARD_TYPE.TABLE.CELL.HAS_DEBIT') },
    { id: 'hasCredit', disablePadding: false, align: 'center', label: t('CARD_TYPE.TABLE.CELL.HAS_CREDIT') },
    { id: 'status', disablePadding: false, label: t('CARD_TYPE.TABLE.CELL.STATUS') },
    { id: 'updatedBy', disablePadding: false, label: t('CARD_TYPE.TABLE.CELL.UPDATED_BY') },
    { id: 'action', disablePadding: false, label: 'Action' }
  ]

  const renderData = (objData: any, no: number) => {
    no = page * pageLimit - pageLimit + no + 1
    const { cardTypeId: cdTypeId, cardTypeShortname: cdTypeShortName, cardTypeName: cdTypeName, hasDebit: cdTypeHasDebit, hasCredit: cdTypeHasCredit, image: cdImage, cardTypeLogo: cdLogo, updatedBy, updatedAt, status: cdTypeStatus } = objData
    const renderImage = (img: any, valueBkLogo: string) => {
      if (!_.isEmpty(img) && !_.isEmpty(img.filePath) && !_.isEmpty(img.fileName)) return `${fileUrl}/${img.filePath}/${img.fileName}`
      else if (valueBkLogo) return `${fileUrl}/${valueBkLogo}`
      else return imgs.errNoImg
    }
    const statusBtnActive = cdTypeStatus === 'ACTIVE' ? true : permissions.isUpdate.disabled
    const statusBtnInActive = cdTypeStatus === 'INACTIVE' ? true : permissions.isUpdate.disabled

    const objRenderData = {
      key: cdTypeId,
      id: cdTypeId,
      obj: objData,
      columns: [
        { option: 'TEXT', align: 'center', label: no },
        {
          option: 'COMPONENT', align: 'center', component: (
            <Box display="flex" justifyContent="center" alignItems="center">
              <Box
                component="img"
                sx={{ width: 35, height: 35, objectFit: 'cover' }}
                src={renderImage(cdImage, cdLogo)}
                onError={onImgError}
                alt=""
              />
            </Box>
          )
        },
        { option: 'TEXT', align: 'left', label: cdTypeShortName },
        { option: 'TEXT', align: 'left', label: cdTypeName },
        {
          option: 'COMPONENT', align: 'center', component: (
            <Box>
              <CheckboxButton
                disabled
                defaultChecked={cdTypeHasDebit === "SUPPORT"}
                checked={cdTypeHasDebit === 'SUPPORT' ? true : false}
                onChange={() => null} />
            </Box>
          )
        },
        {
          option: 'COMPONENT', align: 'center', component: (
            <Box>
              <CheckboxButton
                disabled
                defaultChecked={cdTypeHasCredit === "SUPPORT"}
                checked={cdTypeHasCredit === 'SUPPORT' ? true : false}
                onChange={() => null} />
            </Box>
          )
        },
        { option: 'STATUS', align: 'center', label: cdTypeStatus },
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt } },
        {
          option: 'ACTION',
          align: 'center',
          label: 'action',
          values: [
            { option: 'STATUS_ACTIVE', label: t(`STATUS.ACTIVE`), disabled: statusBtnActive },
            { option: 'STATUS_INACTIVE', label: t(`STATUS.INACTIVE`), disabled: statusBtnInActive },
            { option: 'DIVIDER', label: '', disabled: false },
            { option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: permissions.isUpdate.disabled },
            { option: 'DELETE', label: t(`BUTTON.DELETE`), disabled: permissions.isDelete.disabled }
          ]
        }
      ]
    }
    return (
      <TableRowCommon
        {...objRenderData}
        onactive={() => handleSubmitModal(Number(objRenderData.id), 'ACTIVE', objRenderData.obj)}
        oninactive={() => handleSubmitModal(Number(objRenderData.id), 'INACTIVE', objRenderData.obj)}
        onedit={() => onEdit(objRenderData.obj)}
        ondelete={() => handleSubmitModal(Number(objRenderData.id), 'DELETE', objRenderData.obj)}
      />
    )
  }

  const handleSubmitModal = (id: number, status: string, obj?: any) => {
    setStatusAlertModal(status)
    setId(id)
    setIsOpenAlertModal(true)
    setName(obj.cardTypeName)
  }

  return (
    <Box>
      <AlertModal
        isOpen={isOpenAlertModal}
        title={t(`CARD_TYPE.ALERT.${statusAlertModal === 'DELETE' ? 'TITLE_DELETE' : 'TITLE_ACTIVE'}`)}
        message={t(`CARD_TYPE.ALERT.${statusAlertModal === 'DELETE' ? 'CONFIRM_DELETE_' : 'CONFIRM_STATUS'}`, { expenseTypeName: name, status: t(`CARD_TYPE.ALERT.${statusAlertModal}`) })}
        type={statusAlertModal === 'DELETE' ? 'error' : 'warning'}
        handleClose={() => setIsOpenAlertModal(false)}
        onCancel={{ onClick: () => setIsOpenAlertModal(false), title: t('BUTTON.CANCEL') }}
        onSubmit={{ onClick: statusAlertModal === 'DELETE' ? onDelete : onActive, title: t('BUTTON.SUBMIT') }}
      />
      <SnackbarComponent
        onOpen={isOpenSnack}
        onClose={() => setIsOpenSnack(false)}
        label={labelSnack}
        type={typeSnack}
        transform={`translateX(calc(100vw - 100% - 32px)) translateY(calc(-100vh + 100% + ${window.innerHeight - 50}px))`}
        autoHideTime={3000}
      />
      <HeaderCard text={t('CARD_TYPE.TITLE')} />
      <Box paddingX="24px" paddingTop="16px">
        <Grid container spacing="16px">
          <Grid item xs={2}>
            <TextInput
              label={t('CARD_TYPE.SEARCH')}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
              }}
              fullWidth
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end" >
                    {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                      <FontAwesomeIcon
                        icon={faTimes}
                        onClick={() => {
                          setSearch('');
                        }}
                        style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={2}>
            <SelectInput
              onChange={(event) => {
                const value = event.target.value
                if (value) {
                  handleChangeStatus(value)
                }
              }}
              options={[
                { value: 'ALL', name: t('STATUS.ALL') },
                { value: 'ACTIVE', name: t('STATUS.ACTIVE') },
                { value: 'INACTIVE', name: t('STATUS.INACTIVE') }
              ]}
              label={t('STATUS.LABEL')}
              value={status}
              width="100%"
            />
          </Grid>
          <Grid item xs={1}>
            <MainButton
              title={t('INPUT.SEARCH')}
              type='primary'
              variant='contained'
              style={{ width: '120px' }}
              onClick={() => {
                setPage(1)
                loadData()
              }} />
          </Grid>
          <Grid item xs={7} display="flex" justifyContent="flex-end">
            <MainButton
              title={t('CARD_TYPE.BUTTON.ADD')}
              disabled={permissions.isCreate.disabled}
              type='primary'
              variant='contained'
              onClick={() => setIsPopup(true)}
              startIcon={<AddCircleIcon />}
            />
          </Grid>
        </Grid>
        <Box paddingTop="24px">
          <TableCustom
            page={page}
            pageLimit={pageLimit}
            sortType={sortType}
            sortBy={sortBy}
            onSort={onRequestSort}
            setPageLimit={handleChangeRowsPerPage}
            setPage={handleChangePage}
            rowCount={rowCount}
            headCells={headCells}
            rowsData={cardTypes.map((val, i) => {
              return renderData(val, i)
            })}
          />
        </Box>
        <PageModal
          title={cardTypeId ? t('CARD_TYPE.TITLE_UPDATE') : t('CARD_TYPE.TITLE_CREATE')}
          isOpen={isPopup}
          onCancel={{ onClick: () => resetForm() }}
          onSubmit={{ onClick: () => cardTypeId ? submitEdit() : submit(), disabled: disabledSubmit }}
          handleClose={resetForm}
          size={'XS'}
          overflow
        >
          <Box py={2}>
            {cardTypeId ? (
              <Box display="flex" justifyContent="space-between" alignItems="center" pb={1}>
                <Typography>{t('INPUT.LABEL_STATUS')}</Typography>
                <SwitchButton
                  onChange={() => setCardTypeStatus(cardTypeStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')}
                  checked={cardTypeStatus === 'ACTIVE' ? true : false}
                />
              </Box>
            ) : (
              ''
            )}

            <Box display={'flex'} flexDirection={'column'} gap={'24px'}>
              <Box>
                <UploadImage setValue={uploadImage} pathFile={imgData} />
              </Box>

              <Box>
                <TextInput
                  fullWidth
                  onChange={(event) => {
                    setCardTypeShortname(event.target.value)
                    clearErrorMessage()
                  }}
                  inputProps={{ maxLength: 100 }}
                  value={cardTypeShortname}
                  label={t('CARD_TYPE.INPUT.CARD_TYPE_SHORTNAME')}
                  helperText={
                    errorMessage.CARD_TYPE_SHORTNAME ||
                    errorMessage.IS_DUPLICATE_CARD_TYPE_SHORTNAME ||
                    errorMessage.CARD_TYPE_SHORTNAME_STRING_BASE ||
                    errorMessage.CARD_TYPE_SHORTNAME_STRING_EMPTY ||
                    errorMessage.CARD_TYPE_SHORTNAME_MAXIMUM_LENGTH ||
                    errorMessage.CARD_TYPE_SHORTNAME_ANY_REQUIRED
                  }
                  required={true}
                />
              </Box>

              <Box>
                <TextInput
                  fullWidth
                  onChange={(event) => {
                    setCardTypeName(event.target.value)
                    clearErrorMessage()
                  }}
                  inputProps={{ maxLength: 150 }}
                  value={cardTypeName}
                  label={t('CARD_TYPE.INPUT.CARD_TYPE_NAME')}
                  helperText={
                    errorMessage.CARD_TYPE_NAME ||
                    errorMessage.CARD_TYPE_NAME_STRING_BASE ||
                    errorMessage.CARD_TYPE_NAME_STRING_EMPTY ||
                    errorMessage.CARD_TYPE_NAME_MAXIMUM_LENGTH ||
                    errorMessage.CARD_TYPE_NAME_ANY_REQUIRED ||
                    errorMessage.IS_DUPLICATE_CARD_TYPE_NAME
                  }
                  required={true}
                />
              </Box>

              <Box display={'flex'} flexDirection={'column'} gap={'16px'}>
                <Box>{t('CARD_TYPE.LABEL.SERVICE')}</Box>
                <Box display="flex" flexWrap="wrap" sx={{ 'label': { flexBasis: '50%', margin: 0 } }}>
                  <CheckboxButton
                    label={t('CARD_TYPE.LABEL.DEBIT')}
                    checked={hasDebit === 'SUPPORT' ? true : false}
                    onChange={(event) => {
                      setHasDebit(event.target.checked ? 'SUPPORT' : 'NOT_SUPPORT')
                    }}
                  />
                  <CheckboxButton
                    label={t('CARD_TYPE.LABEL.CREDIT')}
                    checked={hasCredit === 'SUPPORT' ? true : false}
                    onChange={(event) => {
                      setHasCredit(event.target.checked ? 'SUPPORT' : 'NOT_SUPPORT')
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </PageModal>
      </Box>
    </Box>
  )
}
